<script lang="ts">
    import type { ComponentType } from 'svelte';
    import { toast } from '@zerodevx/svelte-toast';
    import { twMerge } from 'tailwind-merge';
    import { AllibeeIcon } from '../../common';
    import type { ToastType } from '../utils/toast';

    export let message = '';
    export let title: string | undefined = undefined;
    export let description: string | undefined = undefined;
    export let descriptionComponent: ComponentType | undefined = undefined;
    export let descriptionProps: Record<string, unknown> = {};
    export let toastType: ToastType = 'default';
    export let toastId: number | undefined = undefined;
    export let actionText: string | undefined = undefined;
    export let onAction: (() => void | Promise<void>) | undefined = undefined;
    export let onDismiss: (() => void) | undefined = undefined;
    export let actionComponent: ComponentType | undefined = undefined;
    export let actionProps: Record<string, unknown> = {};

    const TOAST_TYPE_CONFIG: Record<
        ToastType,
        {
            iconName: string;
            iconClass: string;
            shadowColor: string;
            showCloseButton: boolean;
            type: 'outline' | 'fill';
        }
    > = {
        default: {
            iconName: 'toast-allibee',
            iconClass: 'h-[0.6875rem] w-[1rem] text-[#2D2D41]',
            shadowColor: 'rgba(0, 199, 125, 1)',
            showCloseButton: true,
            type: 'fill',
        },
        success: {
            iconName: 'check-circle',
            iconClass: 'h-[1.125rem] w-[1.125rem] text-[#636570]',
            shadowColor: 'rgba(0, 199, 125, 1)',
            showCloseButton: true,
            type: 'fill',
        },
        fail: {
            iconName: 'exclamation',
            iconClass: 'h-[1.125rem] w-[1.125rem] text-[#636570]',
            shadowColor: 'rgba(232, 44, 0, 1)',
            showCloseButton: true,
            type: 'fill',
        },
        spinner: {
            iconName: 'spinner',
            iconClass: 'h-3 w-3 animate-spin text-gray-300',
            shadowColor: 'rgba(0, 199, 125, 1)',
            showCloseButton: false,
            type: 'outline',
        },
        progress: {
            iconName: 'cloud-download',
            iconClass: 'h-[1.125rem] w-[1.125rem] text-[#636570]',
            shadowColor: 'rgba(0, 199, 125, 1)',
            showCloseButton: false,
            type: 'fill',
        },
    };

    $: hasDescription = Boolean(description || descriptionComponent);
    $: toastTypeConfig = TOAST_TYPE_CONFIG[toastType];

    const dismissToast = () => {
        if (toastId !== null && toastId !== undefined) {
            toast.pop(toastId);
        }

        try {
            onDismiss?.();
        } catch (error) {
            console.error(error);
        }
    };

    const handleActionClick = async () => {
        if (!onAction) return;

        try {
            await onAction();
        } catch (error) {
            console.error(error);
            throw error;
        } finally {
            dismissToast();
        }
    };
</script>

<div
    class="liquid-glass-bordered flex w-[calc(100vw-2rem)] max-w-[22.75rem] items-start gap-2 overflow-hidden rounded-xl border border-[#E1E3EB] bg-white/[0.66] p-2 backdrop-blur-sm"
    style="
    box-shadow: 0rem 0.125rem 0.625rem 0rem rgba(73, 74, 114, 0.16);
    "
>
    <div
        class="flex h-7 w-7 shrink-0 items-center justify-center rounded-[1.25rem] border border-white bg-white/[0.66] backdrop-blur-sm"
        style={`box-shadow: -0.5rem -0.8125rem 5.875rem 0rem ${toastTypeConfig.shadowColor};`}
    >
        <AllibeeIcon name={toastTypeConfig.iconName} type={toastTypeConfig.type} class={toastTypeConfig.iconClass} />
    </div>

    <div class={twMerge('flex min-h-7 min-w-0 flex-1 flex-col items-start justify-center', hasDescription ? 'gap-2.5' : 'gap-1.5')}>
        <div class="flex w-full min-w-0 flex-col items-start justify-center">
            {#if title || message}
                <div class="w-full whitespace-pre-wrap break-words text-sm font-semibold text-[#2D2D41] lg:break-normal">
                    {@html title || message}
                </div>
            {/if}
            {#if descriptionComponent}
                <div class="mt-0.5 w-full min-w-0 text-xs font-normal text-[#2D2D41]">
                    <svelte:component this={descriptionComponent} {...descriptionProps} />
                </div>
            {:else if description}
                <div class="mt-0.5 w-full whitespace-pre-wrap break-words text-xs font-normal text-[#2D2D41] lg:break-normal">
                    {@html description}
                </div>
            {/if}
        </div>
        {#if actionComponent}
            <div class="w-full">
                <svelte:component this={actionComponent} {...actionProps} />
            </div>
        {:else if actionText && onAction}
            <button class="flex items-center gap-1 text-xs font-medium text-[#2D2D41] hover:underline" on:click={handleActionClick}>
                {actionText}
            </button>
        {/if}
    </div>

    {#if toastTypeConfig.showCloseButton}
        <button class="flex h-7 w-7 shrink-0 items-center justify-center" aria-label="닫기" on:click={dismissToast}>
            <AllibeeIcon name="x" type="outline" class="h-3 w-3 text-gray-600"></AllibeeIcon>
        </button>
    {/if}
</div>
